'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { CheckIcon, TriangleAlertIcon, XIcon } from '@repo/ui/icons';
import { AnimatePresence, motion, useReducedMotion } from '@repo/ui/motion';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTableState } from '../context/table-state-provider.tsx';
import type { TableBulkActionContext, TableBulkActionsOptions } from '../context/table-state-context';
import type { RowData } from '../tanstack-table';
export type { TableBulkAction, TableBulkActionContext, TableBulkActionsOptions } from '../context/table-state-context';
export type TableBulkActionBarProps = {
  /** Called after clearing selection, allowing the parent to restore checkbox focus. */
  restoreFocus: () => void;
};
const railMotion = {
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: 8
  },
  initial: {
    opacity: 0,
    y: 8
  },
  transition: {
    duration: 0.1,
    ease: 'easeOut' as const
  }
};

/** A disabled control with a reason shows it in a tooltip; one without stays as it is. */
const WithDisabledReason = t0 => {
  const $ = _c(3);
  const {
    children,
    reason
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== reason) {
    t1 = reason ? <Tooltip><TooltipTrigger render={<span className="inline-flex cursor-not-allowed" tabIndex={0}>{children}</span>} /><TooltipContent>{reason}</TooltipContent></Tooltip> : children;
    $[0] = children;
    $[1] = reason;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const getRowLabel = <TData extends RowData,>(bulkActions: TableBulkActionsOptions<TData>, count: number) => {
  const {
    plural,
    singular
  } = bulkActions.itemLabel;
  return `${count} ${count === 1 ? singular : plural} selected`;
};
export const TableBulkActionBar = t0 => {
  const $ = _c(19);
  const {
    restoreFocus
  } = t0;
  const {
    bulkActions,
    rowSelection,
    table
  } = useTableState();
  const [pendingActionId, setPendingActionId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const prefersReducedMotion = useReducedMotion();
  const loadedRows = table.getRowModel().rows;
  let t1;
  if ($[0] !== rowSelection) {
    t1 = row => Boolean(rowSelection[row.id]);
    $[0] = rowSelection;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedRows = loadedRows.filter(t1);
  const selectedRowIds = selectedRows.map(_temp);
  const selectionKey = Object.keys(rowSelection).sort().join("|");
  const actionClearedSelectionKey = useRef(null);
  const previousSelectionKey = useRef(selectionKey);
  const rowSelectionRef = useRef(rowSelection);
  let t2;
  let t3;
  if ($[2] !== rowSelection) {
    t2 = () => {
      rowSelectionRef.current = rowSelection;
    };
    t3 = [rowSelection];
    $[2] = rowSelection;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  useEffect(() => {
    const selectionChanged = selectionKey !== previousSelectionKey.current;
    const selectionChangeIsFromAction = selectionKey === actionClearedSelectionKey.current;
    if (selectionChanged && selectionChangeIsFromAction) {
      actionClearedSelectionKey.current = null;
    } else {
      if (selectionChanged && feedback?.status === "success" && selectedRows.length) {
        setFeedback(null);
      }
    }
    previousSelectionKey.current = selectionKey;
  }, [feedback?.status, selectedRows.length, selectionKey]);
  let t4;
  if ($[5] !== feedback?.status) {
    t4 = () => {
      if (feedback?.status !== "success") {
        return;
      }
      const timeoutId = setTimeout(() => setFeedback(null), 2000);
      return () => clearTimeout(timeoutId);
    };
    $[5] = feedback?.status;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== feedback) {
    t5 = [feedback];
    $[7] = feedback;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== table) {
    t6 = rowIds => {
      table.setRowSelection(current => {
        const targetIds = new Set(rowIds);
        return Object.fromEntries(Object.entries(current).filter(t7 => {
          const [rowId] = t7;
          return !targetIds.has(rowId);
        }));
      });
    };
    $[9] = table;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const clearSelection = t6;
  let t7;
  if ($[11] !== restoreFocus || $[12] !== table) {
    t7 = () => {
      actionClearedSelectionKey.current = null;
      table.setRowSelection({});
      setFeedback(null);
      restoreFocus();
    };
    $[11] = restoreFocus;
    $[12] = table;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const clearAndRestoreFocus = t7;
  let t8;
  if ($[14] !== clearSelection || $[15] !== restoreFocus) {
    const clearActionTargetAndRestoreFocus = rowIds_0 => {
      const remainingRowIds = Object.keys(rowSelectionRef.current).filter(rowId_0 => !rowIds_0.includes(rowId_0));
      actionClearedSelectionKey.current = remainingRowIds.sort().join("|");
      clearSelection([...rowIds_0]);
      if (remainingRowIds.length === 0) {
        restoreFocus();
      }
    };
    t8 = (action, snapshot) => {
      actionClearedSelectionKey.current = null;
      setPendingActionId(action.id);
      setFeedback(null);
      Promise.resolve().then(() => action.onAction(snapshot)).then(actionResult => {
        if (!actionResult) {
          clearActionTargetAndRestoreFocus(snapshot.rowIds);
          setPendingActionId(current_0 => current_0 === action.id ? null : current_0);
          return;
        }
        if (actionResult.status === "error") {
          setFeedback({
            actionId: action.id,
            message: actionResult.message,
            retry: actionResult.retry === true,
            status: "error"
          });
        } else {
          if (actionResult.clearSelection !== false) {
            clearActionTargetAndRestoreFocus(snapshot.rowIds);
          }
          if (actionResult.message) {
            setFeedback({
              actionId: action.id,
              message: actionResult.message,
              retry: false,
              status: "success"
            });
          }
        }
        setPendingActionId(current_1 => current_1 === action.id ? null : current_1);
      }, () => setPendingActionId(current_2 => current_2 === action.id ? null : current_2));
    };
    $[14] = clearSelection;
    $[15] = restoreFocus;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const runAction = t8;
  const currentSnapshot = {
    clearActionTarget: () => clearSelection(selectedRowIds),
    rowIds: [...selectedRowIds],
    rows: selectedRows.map(_temp2)
  };
  const feedbackAction = feedback && bulkActions?.actions.find(action_0 => action_0.id === feedback.actionId);
  const retryDisabledReason = feedbackAction?.getDisabledReason?.(currentSnapshot);
  const showBar = Boolean(bulkActions && (selectedRows.length > 0 || feedback));
  const t9 = showBar && bulkActions && <motion.div animate={prefersReducedMotion ? {
    opacity: 1
  } : railMotion.animate} className="@container/table-bulk-actions pointer-events-none absolute inset-x-0 bottom-4 z-50" exit={prefersReducedMotion ? {
    opacity: 0
  } : railMotion.exit} initial={prefersReducedMotion ? {
    opacity: 0
  } : railMotion.initial} transition={railMotion.transition}><div aria-label="Bulk actions" className="pointer-events-auto relative left-1/2 flex h-12 w-fit max-w-[calc(100%-2rem)] -translate-x-1/2 items-center overflow-x-auto rounded-md bg-[#171717] p-1 text-white shadow-[inset_0_0_0_1px_#171717,0_2px_4px_-2px_rgb(0_0_0_/_10%),0_4px_6px_-1px_rgb(0_0_0_/_10%)] dark:bg-white dark:text-[#171717] dark:shadow-[inset_0_0_0_1px_#e5e5e5,0_2px_4px_-2px_rgb(0_0_0_/_10%),0_4px_6px_-1px_rgb(0_0_0_/_10%)]" data-slot="table-bulk-action-bar" role="toolbar">{feedback ? <div aria-live="polite" className="flex h-10 shrink-0 items-center gap-1 rounded-xs p-2 text-sm font-normal" role={feedback.status === "error" ? "alert" : "status"}>{feedback.status === "success" ? <span className="flex size-6 items-center justify-center"><CheckIcon aria-hidden="true" className="size-4 text-emerald-400 dark:text-emerald-600" /></span> : <span className="flex size-6 items-center justify-center"><TriangleAlertIcon aria-hidden="true" className="size-4 text-red-300 dark:text-red-600" /></span>}<span>{feedback.message}</span>{feedback.status === "error" && feedback.retry && feedbackAction && selectedRows.length > 0 && <WithDisabledReason reason={retryDisabledReason}><Button aria-label={`Retry ${feedbackAction.label}`} className="h-10 gap-1 rounded-xs p-2 text-white hover:bg-white/10 hover:text-white dark:text-[#171717] dark:hover:bg-black/10 dark:hover:text-[#171717]" disabled={pendingActionId !== null || Boolean(retryDisabledReason)} onClick={() => runAction(feedbackAction, currentSnapshot)} size="sm" variant="ghost">Retry</Button></WithDisabledReason>}</div> : <div aria-label={getRowLabel(bulkActions, selectedRows.length)} aria-live="polite" className="flex h-10 shrink-0 items-center gap-1 rounded-xs p-2 text-sm font-normal" role="status"><span className="flex size-6 items-center justify-center"><CheckIcon aria-hidden="true" className="size-4" /></span><span aria-hidden="true" className="@max-[36rem]/table-bulk-actions:hidden" data-slot="bulk-action-selection-label">{getRowLabel(bulkActions, selectedRows.length)}</span><span aria-hidden="true" className="hidden tabular-nums @max-[36rem]/table-bulk-actions:inline" data-slot="bulk-action-selection-count">{selectedRows.length}</span></div>}{selectedRows.length > 0 && <div className="flex h-[30px] shrink-0 items-center border-l border-[#e5e5e5] dark:border-[#171717]">{bulkActions.actions.map((action_1, index) => {
          const disabledReason = action_1.getDisabledReason?.(currentSnapshot);
          const disabled = pendingActionId !== null || Boolean(disabledReason);
          const Icon = action_1.icon;
          const button = <Button aria-label={action_1.label} className="h-10 gap-1 rounded-xs p-2 text-sm font-normal text-white hover:bg-white/10 hover:text-white dark:text-[#171717] dark:hover:bg-black/10 dark:hover:text-[#171717]" disabled={disabled} onClick={() => {
            if (disabled) {
              return;
            }
            runAction(action_1, currentSnapshot);
          }} size="default" title={action_1.label} variant="ghost"><span className="flex size-6 items-center justify-center"><Icon aria-hidden="true" className="size-4" /></span><span className="@max-[36rem]/table-bulk-actions:hidden" data-slot="bulk-action-label">{action_1.label}</span></Button>;
          return <span className="flex items-center" key={action_1.id}>{index > 0 && <span aria-hidden="true" className="h-[30px] border-l border-[#e5e5e5] dark:border-[#171717]" />}<WithDisabledReason reason={disabledReason}>{button}</WithDisabledReason></span>;
        })}</div>}{selectedRows.length > 0 && <div className="h-[30px] border-l border-[#e5e5e5] dark:border-[#171717]" />}{selectedRows.length > 0 && <Button aria-label="Clear selection" className="size-10 rounded-xs p-2 text-white hover:bg-white/10 hover:text-white dark:text-[#171717] dark:hover:bg-black/10 dark:hover:text-[#171717]" disabled={pendingActionId !== null} onClick={clearAndRestoreFocus} size="icon" title="Clear selection" variant="ghost"><span className="flex size-6 items-center justify-center"><XIcon aria-hidden="true" className="size-4" /></span></Button>}</div></motion.div>;
  let t10;
  if ($[17] !== t9) {
    t10 = <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="TableBulkActionBar" data-sentry-source-file="table-bulk-action-bar.tsx">{t9}</AnimatePresence>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
function _temp(row_0) {
  return row_0.id;
}
function _temp2(row_1) {
  return row_1.original;
}